!(function () {
  "use strict";

  const TEMPLATE_SELECTOR = "[data-video-template]";
  const LOADING_CLASS = "is-loading";
  const FALLBACK_CLASS = "is-fallback";
  const ID_ATTR = "data-video-id";
  const READY_TIMEOUT_MS = 3000;

  if (customElements.get("video-background")) return;

  customElements.define(
    "video-background",
    class extends HTMLElement {
      constructor() {
        super();
        this._markReady = this._markReady.bind(this);
        this._markFallback = this._markFallback.bind(this);
        this._onPowerSaverInteract = this._onPowerSaverInteract.bind(this);
      }

      connectedCallback() {
        this.videoId = this.getAttribute(ID_ATTR);
        this.videoTemplate = this.querySelector(TEMPLATE_SELECTOR);
        this.video = null;
        this._readyHandled = false;
        if (!this.videoId || !this.videoTemplate) return;
        this._setupTemplateObserver();
      }

      _setupTemplateObserver() {
        this.templateObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this._injectVideo();
              observer.unobserve(entry.target);
            });
          },
          { root: null, rootMargin: "300px", threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
        );
        this.templateObserver.observe(this);
      }

      _injectVideo() {
        // Inject template HTML but DO NOT remove is-loading yet.
        // Reveal video only when it is *actually playing* (playing event OR
        // play() promise resolved). loadeddata/canplay only mean data is
        // present; on iOS Safari with autoplay blocked, those can fire before
        // play() rejects, leaving a frozen first frame visible if we use
        // them as ready signals.
        this.innerHTML = this.videoTemplate.innerHTML;
        this.video = this.querySelector("video");
        if (!this.video) {
          this._markFallback();
          return;
        }

        // The only event that guarantees playback has started.
        this.video.addEventListener("playing", this._markReady);
        this.video.addEventListener("error", this._markFallback);

        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise
            .then(() => this._markReady())
            .catch(() => {
              // iOS Low Power Mode / Safari autoplay block — leave poster visible
              // and listen for first user interaction to start playback.
              this._markFallback();
              document.addEventListener("click", this._onPowerSaverInteract, {
                passive: true,
                bubbles: true,
                once: true,
              });
              document.addEventListener("touchstart", this._onPowerSaverInteract, {
                passive: true,
                bubbles: true,
                once: true,
              });
            });
        }

        // Hard fallback — if nothing resolves within 3s, check actual playback.
        // readyState >= 2 alone is not enough; we require !paused to confirm
        // the video is actively playing, not just buffered.
        this._readyTimeoutId = window.setTimeout(() => {
          if (this._readyHandled) return;
          if (this.video && this.video.readyState >= 2 && !this.video.paused) {
            this._markReady();
          } else {
            this._markFallback();
          }
        }, READY_TIMEOUT_MS);

        this._setupPlayPauseObserver();
      }

      _markReady() {
        if (this._readyHandled) return;
        this._readyHandled = true;
        window.clearTimeout(this._readyTimeoutId);
        this.classList.remove(LOADING_CLASS);
        this.classList.remove(FALLBACK_CLASS);
      }

      _markFallback() {
        // Do NOT set _readyHandled here — fallback is a "show poster for now"
        // state, not a terminal state. If the user later interacts and the
        // video starts playing, the 'playing' event still fires and
        // _markReady() takes over.
        window.clearTimeout(this._readyTimeoutId);
        this.classList.remove(LOADING_CLASS);
        this.classList.add(FALLBACK_CLASS);
      }

      _onPowerSaverInteract() {
        if (!this.video) return;
        const p = this.video.play();
        if (p && typeof p.then === "function") {
          // Don't toggle class here directly; let the 'playing' event drive
          // _markReady() so state transitions stay consistent.
          p.then(() => {}).catch(() => {});
        }
      }

      _setupPlayPauseObserver() {
        if (!this.video) return;
        this.playObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!this.video) return;
              if (entry.isIntersecting && typeof this.video.play === "function") {
                const p = this.video.play();
                if (p && typeof p.then === "function") {
                  p.then(() => {}).catch(() => {});
                }
              } else if (!entry.isIntersecting && typeof this.video.pause === "function") {
                this.video.pause();
              }
            });
          },
          { rootMargin: "0px", threshold: [0, 1] }
        );
        this.playObserver.observe(this.video);
      }

      disconnectedCallback() {
        if (this.templateObserver) this.templateObserver.disconnect();
        if (this.playObserver) this.playObserver.disconnect();
        window.clearTimeout(this._readyTimeoutId);
        document.removeEventListener("click", this._onPowerSaverInteract);
        document.removeEventListener("touchstart", this._onPowerSaverInteract);
      }
    }
  );
})();
